export const WARNING_AUDIO_ASSET_PATH = '/audio/alert-tone.mp3';

export type WarningAudioStatus = 'locked' | 'enabled' | 'muted' | 'unavailable';

type AudioFactory = (source: string) => HTMLAudioElement | undefined;

export class WarningAudioController {
  private audio?: HTMLAudioElement;
  private listeners = new Set<(status: WarningAudioStatus) => void>();
  private lastPlayedAt = Number.NEGATIVE_INFINITY;
  private playing = false;
  private muted = false;
  private currentStatus: WarningAudioStatus = 'locked';

  constructor(
    private readonly createAudio: AudioFactory,
    private readonly now: () => number = Date.now,
    private readonly cooldownMs = 2500,
    private readonly source = WARNING_AUDIO_ASSET_PATH
  ) {}

  get status(): WarningAudioStatus {
    return this.currentStatus;
  }

  subscribe(listener: (status: WarningAudioStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.currentStatus);
    return () => this.listeners.delete(listener);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) this.stopPlayback();
    this.setStatus(muted ? 'muted' : this.audio ? 'enabled' : 'locked');
  }

  async initialize(): Promise<boolean> {
    try {
      if (!this.audio) {
        this.audio = this.createAudio(this.source);
        if (!this.audio) {
          this.setStatus('unavailable');
          return false;
        }
        this.audio.preload = 'auto';
        this.audio.loop = false;
        this.audio.volume = 1;
        this.audio.onended = () => { this.playing = false; };
        this.audio.load();
      }
      this.setStatus(this.muted ? 'muted' : 'enabled');
      return true;
    } catch {
      this.audio = undefined;
      this.setStatus('unavailable');
      return false;
    }
  }

  play(force = false): boolean {
    const audio = this.audio;
    const now = this.now();
    if (this.muted || this.currentStatus !== 'enabled' || !audio || this.playing) return false;
    if (!force && now - this.lastPlayedAt < this.cooldownMs) return false;

    try {
      audio.currentTime = 0;
      this.playing = true;
      this.lastPlayedAt = now;
      void audio.play().catch(() => {
        this.playing = false;
        this.setStatus('locked');
      });
      return true;
    } catch {
      this.playing = false;
      this.setStatus('locked');
      return false;
    }
  }

  async test(): Promise<boolean> {
    return (await this.initialize()) && this.play(true);
  }

  dispose(): void {
    this.stopPlayback();
    if (this.audio) {
      this.audio.onended = null;
      this.audio = undefined;
    }
    this.lastPlayedAt = Number.NEGATIVE_INFINITY;
    this.setStatus('locked');
  }

  private stopPlayback(): void {
    if (!this.audio) return;
    this.audio.pause();
    try { this.audio.currentTime = 0; } catch { /* Media metadata may not have loaded yet. */ }
    this.playing = false;
  }

  private setStatus(status: WarningAudioStatus): void {
    this.currentStatus = status;
    this.listeners.forEach((listener) => listener(status));
  }
}

export const warningAudio = new WarningAudioController((source) => (
  typeof Audio === 'undefined' ? undefined : new Audio(source)
));
